import React from "react";
import {
  TextField,
  Button,
  Snackbar,
  Typography,
  Box,
  Paper,
} from "@mui/material";
import { Link } from "react-router-dom";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import { selectIsLoading } from "../../features/userSlice";
import { useSelector } from "react-redux";
import styles from "./ResetPasswordStyles.module.css";
import { Stack } from "@mui/system";

interface ConfirmPasswordUIProps {
  newPassword: string;
  reNewPassword: string;
  passwordError: string | null;
  showPassword: boolean;
  onNewPasswordChange: (value: string) => void;
  onReNewPasswordChange: (value: string) => void;
  onToggleShowPassword: () => void;
  onResetPasswordConfirm: () => void;
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: "success" | "error";
  onSnackbarClose: () => void;
  isSuccess: boolean;
}

const ConfirmPasswordSection: React.FC<ConfirmPasswordUIProps> = ({
  newPassword,
  reNewPassword,
  passwordError,
  showPassword,
  onNewPasswordChange,
  onReNewPasswordChange,
  onToggleShowPassword,
  onResetPasswordConfirm,
  snackbarOpen,
  snackbarMessage,
  snackbarSeverity,
  onSnackbarClose,
  isSuccess,
}) => {
  const loading = useSelector(selectIsLoading);

  return (
    <Stack alignItems="center" flexDirection={"row"}>
      <img
        className={styles.backgroundImage1}
        alt="Background"
        src="/background-image-1@2x.png"
      />
      {/* card */}
      <Stack
        alignItems="center"
        justifyContent={"space-around"}
        sx={{ flex: 1 }}
      >
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            width: "100%",
            maxWidth: 360,
            borderRadius: 2,
            boxShadow: `
      0px 6px 6px -3px rgba(0, 0, 0, 0.2),
      0px 10px 14px 1px rgba(0, 0, 0, 0.14),
      0px 4px 18px 3px rgba(0, 0, 0, 0.12)
    `,
          }}
        >
          <div className={styles.logoSection}>
            <img
              className={styles.optivalLogo1Icon}
              alt=""
              src="/optivallogo-1@2x.png"
            />
          </div>

          {!isSuccess ? (
            <Stack alignItems="flex-start">
              {/* Title */}
              <Typography variant="h4" component="h4" sx={{ marginBlock: 4 }}>
                Set New Password
              </Typography>

              {/* Form */}
              <Box
                component="form"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}
              >
                <TextField
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => onNewPasswordChange(e.target.value)}
                  error={!!passwordError}
                  color="secondary"
                  helperText={
                    passwordError ||
                    "Must be 8 or more characters (English only) and contain at least 1 number and 1 special character."
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={onToggleShowPassword}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: "100%" }}
                />

                <TextField
                  label="Confirm Password"
                  type="password"
                  value={reNewPassword}
                  onChange={(e) => onReNewPasswordChange(e.target.value)}
                  error={!!passwordError}
                  helperText={passwordError || ""}
                  sx={{ width: "100%" }}
                  color="secondary"
                />

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onResetPasswordConfirm}
                  loading={loading}
                  sx={{
                    width: "100%",
                    mt: 2,
                  }}
                >
                  RESET PASSWORD
                </Button>
              </Box>
            </Stack>
          ) : (
            // Success message
            <Box sx={{ textAlign: "left", my: 4 }}>
              <Typography variant="h4" component="h4">
                Password set successfully
              </Typography>
            </Box>
          )}

          {/* Back to Login button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              component={Link}
              to="/login"
              variant="contained"
              color="secondary"
              size="small"
              sx={{ width: "100%" }}
            >
              BACK TO LOGIN
            </Button>
          </Box>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={onSnackbarClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert onClose={onSnackbarClose} severity={snackbarSeverity}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Paper>
      </Stack>
    </Stack>
  );
};

export default ConfirmPasswordSection;
